import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type {
  NotificationSettingsDto,
  NotificationSettingsRequest,
  SimpleProjectDto,
  UserRoleDto,
  UsersMultipleCreateRequest,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { CheckboxGroup } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useSuggestedEmailFix } from "@/helpers/email-validation";
import {
  createEmailValidRule,
  createImageSizeRule,
  createImageTypeRule,
  createPhoneNumberStringRule,
  createRequiredStringRule,
} from "@/helpers/rules";
import { ADMIN_USER_ROLE_TYPES, getRoleLabelByType } from "@/helpers/user-roles";
import { sortAlphabetically } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { portfolioMutations } from "@/queries/portfolio/mutations";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { Trans, useTranslation } from "@/translations";

interface AddAdminFormValues {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  occupation?: string;
  roleType: UserRoleDto["type"];
  avatar: FormImage[];
  hideChat: boolean;
  isOnlyAllowedOwnTickets: boolean;
  isAssignableToTickets: boolean;
  onboardingComplete: boolean;
  projects: SimpleProjectDto[];
  notificationSettings: NotificationSettingsRequest | undefined;
}

const MAX_CHARACTERS = 255;

export function AddAdminPage(): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const postHog = usePostHog();
  const { uploadFormImage } = useUploadImage();
  const sessionUser = useSessionUser();

  const userQueries = useUserQueries();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: defaultNotificationSettings,
    isPending: isPendingDefaultNotificationSettings,
    error: errorDefaultNotificationSettings,
  } = useQuery(userQueries.notificationSettingsDefaults());

  const defaultValues: AddAdminFormValues = {
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    roleType: ADMIN_USER_ROLE_TYPES[0],
    avatar: [],
    hideChat: true,
    isOnlyAllowedOwnTickets: false,
    onboardingComplete: true,
    notificationSettings: { ...defaultNotificationSettings, applyToAllProjects: false },
    isAssignableToTickets: false,
    projects: [],
  };
  const form = useForm<AddAdminFormValues>({
    defaultValues,
    values: defaultValues,
  });
  const currProjects = useWatch({
    name: "projects",
    control: form.control,
  });
  const currNotificationSettings = useWatch({
    name: "notificationSettings",
    control: form.control,
  });

  const portfolioQueries = usePortfolioQueries();

  const {
    data: allowedProjects,
    isPending: isPendingAllowedProjects,
    error: errorAllowedProjects,
  } = useQuery(portfolioQueries.userProjectsCanInviteList());

  const addUsersMutation = portfolioMutations.useAddAdminUsers();

  const handleSubmit = async (formValues: AddAdminFormValues) => {
    setIsSubmitting(true);

    const payload: UsersMultipleCreateRequest = {
      email: formValues.email.trim(),
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      mobileNumber: formValues.mobileNumber ?? "",
      roleType: formValues.roleType,
      projectIds: formValues.projects.map((project) => project.id),
      hideChat: formValues.hideChat,
      isOnlyAllowedOwnTickets: formValues.isOnlyAllowedOwnTickets,
      isAssignableToAllTickets: formValues.isAssignableToTickets,
      onboardingCompleted: formValues.onboardingComplete,
      notificationSettings: formValues.notificationSettings,
    };

    if (formValues.avatar.length > 0) {
      const uploadedImage = await uploadFormImage(formValues.avatar[0]);

      payload.avatarId = uploadedImage?.id;
    }

    await addUsersMutation.mutateAsync(payload);

    form.reset({
      ...defaultValues,
      projects: currProjects,
      notificationSettings: currNotificationSettings,
    });
    setIsSubmitting(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const email = useWatch({ control: form.control, name: "email" });
  const summaryInterval = useWatch({
    control: form.control,
    name: "notificationSettings.sendCommunitySummaryEmailInterval",
  });
  const isSummaryDisabled = summaryInterval === "never";
  const emailSuggestion = useSuggestedEmailFix(email);

  const error = errorAllowedProjects || errorDefaultNotificationSettings;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isPendingAllowedProjects || isPendingDefaultNotificationSettings;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.add-admins.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.add-admins.title"),
            },
          ]}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="100%">
          <Form.Container>
            <Form.Root formMethods={form} onSubmit={handleSubmit}>
              <Tiles columns={{ base: 1, container: { lg: 2 } }} gap="xl">
                <Form.Body>
                  <h3 className="text-heading-3">{t("page.create-user.form.title.basic-info")}</h3>
                  <FormField label={t("page.create-user.form.first-name.label")} required>
                    <FormInput<AddAdminFormValues>
                      name="firstName"
                      placeholder={t("page.create-user.form.first-name.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.create-user.form.first-name.label"),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                          value: MAX_CHARACTERS,
                        },
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.create-user.form.last-name.label")} required>
                    <FormInput<AddAdminFormValues>
                      name="lastName"
                      placeholder={t("page.create-user.form.last-name.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.create-user.form.last-name.label"),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                          value: MAX_CHARACTERS,
                        },
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.create-user.form.mobile.label")}>
                    <FormInput<AddAdminFormValues, "mobileNumber">
                      name="mobileNumber"
                      placeholder={t("page.create-user.form.mobile.placeholder")}
                      type="tel"
                      inputMode="tel"
                      rules={{
                        validate: {
                          isValid: createPhoneNumberStringRule({ t }),
                        },
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.create-user.form.email.label")} required>
                    <FormInput<AddAdminFormValues, "email">
                      name="email"
                      placeholder={t("page.create-user.form.email.placeholder")}
                      type="email"
                      inputMode="email"
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.create-user.form.email.label"),
                          isValid: createEmailValidRule(t),
                        },
                      }}
                    />
                    {emailSuggestion && (
                      <p className="mt-space-xs flex flex-row items-baseline text-text-secondary">
                        <Trans
                          i18nKey="page.create-user.form.email.suggestion"
                          values={{ email: emailSuggestion }}
                          components={{
                            suggestion: (value) => (
                              <span className="ml-space-xs">
                                <Button
                                  variant="ghostPrimary"
                                  onClick={() => {
                                    form.setValue("email", emailSuggestion);
                                    form.clearErrors("email");
                                    postHog.capture("email_suggestion_clicked", {
                                      email: emailSuggestion,
                                    });
                                  }}
                                >
                                  {value}
                                </Button>
                              </span>
                            ),
                          }}
                        />
                      </p>
                    )}
                  </FormField>
                  <FormField label={t("page.create-user.form.projects.label")} required>
                    <FormMultiSelect<AddAdminFormValues, "projects">
                      name="projects"
                      placeholder={t("page.create-user.form.projects.placeholder")}
                      items={allowedProjects || []}
                      searchableFieldSelector={(project) => [project.name, project.owner, project.maintenance]}
                      renderOption={(project) => project.name}
                      keySelector={(project) => project.id}
                      sortSelectedItems={sortAlphabetically}
                      rules={{
                        required: t("components.form.error.required", {
                          inputName: t("page.create-user.form.projects.label"),
                        }),
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.create-user.form.role.label")} required>
                    <FormSelect<AddAdminFormValues, UserRoleDto["type"]>
                      name="roleType"
                      placeholder={t("page.create-user.form.role.placeholder")}
                      items={ADMIN_USER_ROLE_TYPES}
                      keySelector={(x) => x}
                      renderOption={(x) => getRoleLabelByType(t, x)}
                      rules={{
                        required: t("components.form.error.required", {
                          inputName: t("page.create-user.form.role.label"),
                        }),
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.create-user.form.avatar.label")}>
                    <FormImageInput<AddAdminFormValues, "avatar">
                      ref={fileInputRef}
                      name="avatar"
                      rules={{
                        validate: {
                          size: createImageSizeRule({ t }),
                          type: createImageTypeRule({ t }),
                        },
                      }}
                      isAvatarInput
                      cropper
                    />
                  </FormField>
                  {sessionUser.isSuperAdmin && (
                    <>
                      <FormCheckbox<AddAdminFormValues>
                        name="onboardingComplete"
                        label={t("page.create-user.form.onboarding-complete.label")}
                      />
                      <FormCheckbox<AddAdminFormValues>
                        name="isOnlyAllowedOwnTickets"
                        label={t("page.create-user.form.is-only-allowed-own-tickets.label")}
                      />
                      <FormCheckbox<AddAdminFormValues>
                        name="isAssignableToTickets"
                        label={t("page.create-user.form.is-assignable")}
                      />
                      <FormCheckbox<AddAdminFormValues> name="hideChat" label={t("page.create-user.form.chat.label")} />
                    </>
                  )}
                </Form.Body>
                <Form.Body>
                  <h3 className="text-heading-3">{t("page.create-user.form.title.notification-settings")}</h3>
                  <h4 className="text-heading-4">{t("page.notification-settings.group.email")}</h4>
                  <NotificationSettingsSection title={t("page.notification-settings.project-reporting.title")}>
                    <CheckboxGroup label={t("page.notification-settings.project-reporting.monthly-project-report")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.sendMonthlyReportingEmail">
                        label={t("common.label.email")}
                        name="notificationSettings.sendMonthlyReportingEmail"
                      />
                    </CheckboxGroup>
                  </NotificationSettingsSection>
                  <NotificationSettingsSection title={t("page.notification-settings.community-summary-email.title")}>
                    <section className="flex flex-col items-start justify-between gap-x-space-md gap-y-space-sm @md:flex-row @md:items-center">
                      <label htmlFor="send-community-summary-email-interval">
                        {t("page.notification-settings.community-summary-email.frequency.label")}
                      </label>
                      <div className="min-w-selection-group">
                        <FormSelect<AddAdminFormValues, NotificationSettingsDto["sendCommunitySummaryEmailInterval"]>
                          id="send-community-summary-email-interval"
                          name="notificationSettings.sendCommunitySummaryEmailInterval"
                          items={["never", "everyHour", "everyThreeHours", "everyDay", "everyWeek", "everyTwoWeeks"]}
                          keySelector={(x) => x}
                          renderOption={(x) => {
                            switch (x) {
                              case "everyDay":
                                return t("page.notification-settings.form.email.timed-value.every-day");
                              case "everyHour":
                                return t("page.notification-settings.form.email.timed-value.every-hour");
                              case "everyThreeHours":
                                return t("page.notification-settings.form.email.timed-value.every-three-hours");
                              case "everyWeek":
                                return t("page.notification-settings.form.email.timed-value.every-week");
                              case "everyTwoWeeks":
                                return t("page.notification-settings.form.email.timed-value.every-two-weeks");
                              case "never":
                                return t("page.notification-settings.form.email.timed-value.never");
                              default:
                                return x;
                            }
                          }}
                        />
                      </div>
                    </section>
                    <CheckboxGroup label={t("page.notification-settings.community-summary-email.new-post")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.generalMessageNew.emailEnabled">
                        label={t("common.label.email")}
                        name="notificationSettings.generalMessageNew.emailEnabled"
                        disabled={isSummaryDisabled}
                      />
                    </CheckboxGroup>
                    <CheckboxGroup label={t("page.notification-settings.community-summary-email.new-comment")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.generalMessageCommentNew.emailEnabled">
                        label={t("common.label.email")}
                        name="notificationSettings.generalMessageCommentNew.emailEnabled"
                        disabled={isSummaryDisabled}
                      />
                    </CheckboxGroup>
                    <CheckboxGroup label={t("page.notification-settings.community-summary-email.new-chat")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.chatNew.emailEnabled">
                        label={t("common.label.email")}
                        name="notificationSettings.chatNew.emailEnabled"
                        disabled={isSummaryDisabled}
                      />
                    </CheckboxGroup>
                  </NotificationSettingsSection>
                  <NotificationSettingsSection title={t("page.notification-settings.ticket-emails.title")}>
                    <CheckboxGroup label={t("page.notification-settings.ticket-emails.new-ticket-assigned-to-me")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.ticketAssignToMe.emailEnabled">
                        label={t("common.label.email")}
                        name="notificationSettings.ticketAssignToMe.emailEnabled"
                      />
                    </CheckboxGroup>
                    <CheckboxGroup
                      label={t("page.notification-settings.ticket-emails.new-comment-on-ticket-assigned-to-me")}
                    >
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.ticketAssignedCommentNew.emailEnabled">
                        label={t("common.label.email")}
                        name="notificationSettings.ticketAssignedCommentNew.emailEnabled"
                      />
                    </CheckboxGroup>
                  </NotificationSettingsSection>
                  <h4 className="mt-space-md text-heading-4">{t("page.notification-settings.group.dashboard")}</h4>
                  <NotificationSettingsSection title={t("page.notification-settings.dashboard-notifications.title")}>
                    <CheckboxGroup label={t("page.notification-settings.dashboard-notifications.new-post")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.generalMessageNew.dashboardEnabled">
                        label={t("common.label.web")}
                        name="notificationSettings.generalMessageNew.dashboardEnabled"
                      />
                    </CheckboxGroup>
                    <CheckboxGroup label={t("page.notification-settings.dashboard-notifications.new-comment")}>
                      <FormCheckbox<
                        AddAdminFormValues,
                        "notificationSettings.generalMessageCommentNew.dashboardEnabled"
                      >
                        label={t("common.label.web")}
                        name="notificationSettings.generalMessageCommentNew.dashboardEnabled"
                      />
                    </CheckboxGroup>
                    <CheckboxGroup label={t("page.notification-settings.dashboard-notifications.new-chat")}>
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.chatNew.dashboardEnabled">
                        label={t("common.label.web")}
                        name="notificationSettings.chatNew.dashboardEnabled"
                      />
                    </CheckboxGroup>
                    <CheckboxGroup
                      label={t("page.notification-settings.dashboard-notifications.new-ticket-assigned-to-me")}
                    >
                      <FormCheckbox<AddAdminFormValues, "notificationSettings.ticketAssignToMe.dashboardEnabled">
                        label={t("common.label.web")}
                        name="notificationSettings.ticketAssignToMe.dashboardEnabled"
                      />
                    </CheckboxGroup>
                    <CheckboxGroup label={t("page.notification-settings.dashboard-notifications.new-ticket-comment")}>
                      <FormCheckbox<
                        AddAdminFormValues,
                        "notificationSettings.ticketAssignedCommentNew.dashboardEnabled"
                      >
                        label={t("common.label.web")}
                        name="notificationSettings.ticketAssignedCommentNew.dashboardEnabled"
                      />
                    </CheckboxGroup>
                  </NotificationSettingsSection>
                </Form.Body>
              </Tiles>

              <footer className="ml-auto w-fit">
                <Button type="submit" isLoading={isSubmitting} data-testid="add-admins-submit-btn">
                  {t("page.create-user.form.submit.new")}
                </Button>
              </footer>
            </Form.Root>
          </Form.Container>
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}

type NotificationSettingsSectionProps = {
  title: string;
  children: React.ReactNode;
};

function NotificationSettingsSection({ title, children }: NotificationSettingsSectionProps) {
  return (
    <section className="mb-space-md flex flex-col gap-space-sm">
      <h4 className="text-body-emphasis">{title}</h4>
      <Stack gap="sm">{children}</Stack>
    </section>
  );
}
